import type { LaborDay2026SaleMarketTier } from '../utils/get-labor-day-2026-sale-variant';

export type LaborDay2026SaleVariant = 'labor_day_full' | 'labor_day_low';

export const LABOR_DAY_2026_SALE_CTA_LABEL = 'Save Now';

/**
 * Save Now deep link, by market tier. Full-price markets (`sept50`) check out on the 4-month bundle;
 * low-price markets (`sept10`) on the annual plan. Use `getLaborDay2026SaleCheckoutHref` to append the
 * live coupon.
 */
export const LABOR_DAY_2026_SALE_CHECKOUT_HREF: Record<LaborDay2026SaleMarketTier, string> = {
  full: '/checkout/individual/4month/credit-card',
  low: '/checkout/individual/annually/credit-card',
};

type LaborDay2026SaleVariantConfig = {
  backgroundColor: string;
  /** Gradient applied to the first headline line (orange -> purple). */
  catalogBg: string;
  catalogBgMobile: string;
  catalogBorderColor: string;
  /** Gradient stroke on the catalog banner frame (Figma: `#A232FF` left → `#FF8027` right). */
  catalogBorderGradient: string;
  ctaColor: string;
  /** Badge-row art for the experience ATF card (urgency boxes + circular badge; the card's only raster). */
  experienceBadge: string;
  /** Gradient text fill for the emphasized headline line. */
  headlineAccentGradient: string;
  homepageBg: string;
  homepageBgMobile: string;
  /** Standalone circular badge art for the mobile homepage card, rendered right-anchored so it stays crisp at any card height. */
  homepageBadgeMobile: string;
  /** CSS gradient filling the mobile homepage card behind the badge (replaces the baked-in bg image gradient). */
  homepageMobileGradient: string;
  homepageHeadlineLine1: string;
  homepageHeadlineLine2: string;
  /** Third headline segment (white). Wraps to its own line on mobile, inline on desktop. */
  homepageHeadlineLine3: string;
  homepageMobileObjectFit: 'contain' | 'cover';
  homepageMobileObjectPosition: string;
  homepageObjectFit: 'contain' | 'cover';
  homepageObjectPosition: string;
  homepageSubtitle: string;
  /** Second subtitle segment. Breaks to its own line on mobile (Figma explicit <br>), inline on desktop. */
  homepageSubtitleLine2: string;
  experienceHeadline: string;
  experienceHeadlineLine2: string;
  experienceSubtitle: string;
  catalogHeadlineLine1: string;
  catalogHeadlineLine2: string;
  subtitleColor: string;
  tag: string;
  tagColor: string;
};

export const LABOR_DAY_2026_SALE_VARIANTS: Record<LaborDay2026SaleVariant, LaborDay2026SaleVariantConfig> = {
  labor_day_full: {
    backgroundColor: '#000000',
    // The G1 and G2 catalog backgrounds are byte-identical: Figma nodes 2070:6304 (G1 desktop) and
    // 2070:6317 (G2 desktop) share the same navy gradient + grid + noise art (only the headline text
    // differs, which is rendered in code). The G2 tier points at its own folder copy for path clarity.
    catalogBg: '/images/promotions/labor-day-2026/catalog-bg-v2.png',
    catalogBgMobile: '/images/promotions/labor-day-2026/catalog-bg-mobile-v2.png',
    catalogBorderColor: '#A232FF',
    catalogBorderGradient: 'linear-gradient(to right, #A232FF 0%, #FF8027 100%)',
    ctaColor: '#175CFF',
    experienceBadge: '/images/promotions/labor-day-2026/experience-badge.png',
    headlineAccentGradient: 'linear-gradient(to left, #FF8027, #A232FF)',
    homepageBg: '/images/promotions/labor-day-2026/homepage-bg-v2.png',
    homepageBgMobile: '/images/promotions/labor-day-2026/homepage-bg-mobile-v2.png',
    homepageBadgeMobile: '/images/promotions/labor-day-2026/homepage-badge-mobile-v2.png',
    homepageMobileGradient: 'linear-gradient(-28deg, #00298C 0%, rgba(0, 41, 140, 0) 81.9%)',
    homepageHeadlineLine1: 'Flash sale:',
    homepageHeadlineLine2: 'build',
    homepageHeadlineLine3: 'what\u2019s next',
    homepageMobileObjectFit: 'cover',
    homepageMobileObjectPosition: 'right top',
    homepageObjectFit: 'cover',
    homepageObjectPosition: 'right center',
    homepageSubtitle: 'Learn tech skills with Nanodegree programs',
    homepageSubtitleLine2: 'and master\u2019s degrees designed for career growth.',
    experienceHeadline: 'Flash sale:',
    experienceHeadlineLine2: 'build what\u2019s next',
    experienceSubtitle: 'Learn the tech skills employers want.',
    catalogHeadlineLine1: '50% off all courses.',
    catalogHeadlineLine2: 'Learn the AI and tech skills employers want.',
    subtitleColor: '#CECFD4',
    tag: 'Ends soon',
    tagColor: '#E5A5FF',
  },
  labor_day_low: {
    backgroundColor: '#000000',
    // Catalog background art is identical across tiers, so G2 shares the G1 folder's copies.
    catalogBg: '/images/promotions/labor-day-2026/catalog-bg-v2.png',
    catalogBgMobile: '/images/promotions/labor-day-2026/catalog-bg-mobile-v2.png',
    catalogBorderColor: '#A232FF',
    catalogBorderGradient: 'linear-gradient(to right, #A232FF 0%, #FF8027 100%)',
    ctaColor: '#175CFF',
    experienceBadge: '/images/promotions/labor-day-2026-low/experience-badge.png',
    headlineAccentGradient: 'linear-gradient(to left, #FF8027, #A232FF)',
    homepageBg: '/images/promotions/labor-day-2026-low/homepage-bg-v2.png',
    homepageBgMobile: '/images/promotions/labor-day-2026-low/homepage-bg-mobile-v2.png',
    homepageBadgeMobile: '/images/promotions/labor-day-2026-low/homepage-badge-mobile-v2.png',
    homepageMobileGradient: 'linear-gradient(-28deg, #00298C 0%, rgba(0, 41, 140, 0) 81.9%)',
    homepageHeadlineLine1: 'Sitewide sale:',
    homepageHeadlineLine2: 'build',
    homepageHeadlineLine3: 'what\u2019s next',
    homepageMobileObjectFit: 'cover',
    homepageMobileObjectPosition: 'right top',
    homepageObjectFit: 'cover',
    homepageObjectPosition: 'right center',
    homepageSubtitle: 'Learn tech skills with Nanodegree programs',
    homepageSubtitleLine2: 'and master\u2019s degrees designed for career growth.',
    experienceHeadline: 'Sitewide sale:',
    experienceHeadlineLine2: 'build what\u2019s next',
    experienceSubtitle: 'Learn the tech skills employers want.',
    catalogHeadlineLine1: 'Sitewide sale.',
    catalogHeadlineLine2: 'Learn the AI and tech skills employers want.',
    subtitleColor: '#CECFD4',
    tag: 'Ends soon',
    tagColor: '#E5A5FF',
  },
};

/**
 * Variant checkout link with the live coupon appended as `?coupon=<promoCode>`. The coupon also
 * auto-applies from the `COUPON_COOKIE` that `usePromotion` writes; the query param is a fallback
 * in case auto-apply does not run. When absent, the bare tier link is returned.
 */
export function getLaborDay2026SaleCheckoutHref(variant: LaborDay2026SaleVariant, promoCode?: string | null): string {
  const tier: LaborDay2026SaleMarketTier = variant === 'labor_day_low' ? 'low' : 'full';
  const href = LABOR_DAY_2026_SALE_CHECKOUT_HREF[tier];
  if (!promoCode) {
    return href;
  }
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}coupon=${encodeURIComponent(promoCode)}`;
}
